import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { supabase } from '../lib/supabaseClient';
import { translateAuthError } from '../lib/authErrors';
import './Login.css';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const turnstileRef = useRef(null);

  const isValid = email.includes('@') && password.length >= 6 && !!captchaToken;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || loading) return;

    setErrorMsg(null);
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('signin-with-captcha', {
        body: { email, password, captchaToken },
      });

      if (error) {
        const body = await error.context?.json?.().catch(() => null);
        setErrorMsg(translateAuthError(body?.code, body?.error ?? error.message));
      } else {
        // Hydrate le client local avec la session ouverte par l'edge function ;
        // ceci déclenche le listener onAuthStateChange (AuthContext).
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }
    } catch {
      setErrorMsg('Erreur réseau. Vérifiez votre connexion.');
    } finally {
      // Un token Turnstile est à usage unique : on le régénère à chaque tentative.
      turnstileRef.current?.reset();
      setCaptchaToken(null);
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <div className="App-card">
        <h1 className="App-title">
          My<span>Travo</span>
        </h1>
        <p className="App-subtitle">Connectez-vous pour accéder au portail</p>

        <form onSubmit={handleSubmit}>
          <div className="Field">
            <label className="Field-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className="Field-input"
              placeholder="vous@exemple.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="Field">
            <label className="Field-label" htmlFor="password">Mot de passe</label>
            <div className="Field-passwordWrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="Field-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="Field-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.3 20.3 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a20.3 20.3 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            <Link to="/forgot-password" state={{ email }} className="Login-forgot">
              Mot de passe oublié ?
            </Link>
          </div>

          {TURNSTILE_SITE_KEY && (
            <div className="Field-turnstile">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={setCaptchaToken}
                onExpire={() => setCaptchaToken(null)}
                onError={() => setCaptchaToken(null)}
              />
            </div>
          )}

          {errorMsg && <p className="Login-error">{errorMsg}</p>}

          <button
            type="submit"
            className="App-submit"
            disabled={!isValid || loading}
          >
            {loading ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
